import { Check, Sparkles, CalendarClock } from "lucide-react";
import { Link } from "react-router-dom";
import { useState } from "react";
import { api } from "@/lib/api";

/**
 * TIER 2 — Concierge · booking URL.
 *
 * Real Google Calendar appointment-scheduling link (received from the
 * founder). Search here to swap if the link ever changes.
 *
 * The Concierge CTA MUST open a Calendar booking flow — never Razorpay
 * or any direct payment link. Founder speaks with the student first
 * before any payment happens (deliberate qualifying step).
 */
const CONCIERGE_BOOKING_URL =
  "https://calendar.app.google/ZdDj2aY9m8RUQm9LA";

const FREE_FEATURES = [
  "Northstar. Full personalised visa timeline across 19 countries",
  "Cohort. Peer matches, requests, Assembly (arrival / trip / roommate / social coordination), and group thread with your semester",
  "Smart Packing. Country-specific list built from your dates, airline & housing",
  "Every visa fee, funds figure and processing time. Verified, sourced from official government portals",
  "Document checklist + booking-portal links per country",
];

const CONCIERGE_FEATURES = [
  "Document collection & checklist review",
  "Application form completion and review",
  "Visa appointment booking, handled for you",
  "Notarization / apostille guidance where required",
  "Blocked account / financial proof setup guidance",
  "Motivation letter / cover letter review",
  "Mock interview and interview prep",
  "A named case handler, reachable until the visa is issued",
];

export default function Pricing() {
  return (
    <div className="max-w-6xl mx-auto px-4 sm:px-6 pt-14 md:pt-20 pb-24">
      <div className="max-w-3xl">
        <div className="inline-flex items-center gap-2 moe-chip moe-chip-yolk mb-4" data-testid="pricing-eyebrow">
          <Sparkles size={12} /> Two ways in
        </div>
        <h1 className="text-4xl sm:text-5xl lg:text-6xl font-bold tracking-tight leading-[1.02]" data-testid="pricing-h1">
          Free to plan.<br />
          <span className="gradient-underline">Paid, only when you want help doing it.</span>
        </h1>
        <p className="mt-5 text-base sm:text-lg text-muted">
          One outcome. Your visa in your hand. Two ways to get there: do it yourself with our full toolkit,
          or hand it to a named case handler.
        </p>
      </div>

      <div className="mt-14 grid md:grid-cols-2 gap-5 md:gap-6" data-testid="pricing-tiers">

        {/* ============================ FREE ============================ */}
        <div className="border border-ink rounded-[18px] p-7 md:p-8 bg-white flex flex-col" data-testid="tier-free">
          <div className="flex items-baseline justify-between gap-3">
            <div className="text-sm font-bold text-ink">Free</div>
            <div className="moe-chip">Engagement, not revenue</div>
          </div>
          <div className="mt-6 flex items-baseline gap-2">
            <div className="text-5xl font-bold tracking-tight">₹0</div>
            <div className="text-sm text-muted">forever</div>
          </div>
          <p className="mt-3 text-sm text-ink leading-snug">
            Everything you need to plan your exchange yourself, at no cost.
          </p>
          <p className="mt-3 text-xs text-muted italic leading-snug">
            Free, because the more of you here, the better this gets for everyone.
            The Cohort network effect only works with real students on it.
          </p>

          <ul className="mt-6 space-y-3" data-testid="tier-free-features">
            {FREE_FEATURES.map((f) => (
              <li key={f} className="flex items-start gap-2 text-sm text-ink">
                <Check size={16} className="mt-0.5 text-elec shrink-0" strokeWidth={2.4} />
                <span>{f}</span>
              </li>
            ))}
          </ul>

          <Link
            to="/signup"
            data-testid="tier-free-cta"
            className="mt-8 w-full moe-btn moe-btn-ghost"
          >
            Start free
          </Link>
        </div>

        {/* ============================ CONCIERGE ============================ */}
        <div id="tier-concierge" className="border-2 border-ink rounded-[18px] p-7 md:p-8 moe-panel-mint flex flex-col relative overflow-hidden scroll-mt-28" data-testid="tier-concierge">
          <div className="flex items-baseline justify-between gap-3">
            <div className="text-sm font-bold text-ink">Concierge</div>
            <div className="moe-chip moe-chip-elec">Named case handler</div>
          </div>
          <div className="mt-6 flex items-baseline gap-2">
            <div className="text-5xl font-bold tracking-tight">₹19,500</div>
            <div className="text-sm text-muted">one-time</div>
          </div>
          <p className="mt-3 text-sm text-ink leading-snug">
            A specialist takes the whole visa process off your plate. From checklist to appointment to interview prep.
          </p>

          <ul className="mt-6 space-y-3" data-testid="tier-concierge-features">
            {CONCIERGE_FEATURES.map((f) => (
              <li key={f} className="flex items-start gap-2 text-sm text-ink">
                <Check size={16} className="mt-0.5 text-elec shrink-0" strokeWidth={2.4} />
                <span>{f}</span>
              </li>
            ))}
          </ul>

          {/* CTA. Google Calendar booking, NOT Razorpay. Founder qualifies each student first. */}
          <a
            href={CONCIERGE_BOOKING_URL}
            target="_blank"
            rel="noopener noreferrer"
            data-testid="tier-concierge-cta"
            className="mt-8 w-full moe-btn moe-btn-primary inline-flex items-center justify-center gap-2"
          >
            <CalendarClock size={16} />
            Book a 20-min call to start
          </a>
          <p className="mt-3 text-[11px] text-muted leading-snug">
            No payment on this page. We speak with you first to confirm your case is a fit for Concierge before
            any invoice goes out.
          </p>

          {/* Prefer we reach out first? Optional lead capture. Sends a
              confirmation email to the student and a lead notification to
              the team inbox. Never blocks the Calendar CTA above. */}
          <ConciergeLeadForm />
        </div>

      </div>

      <div className="mt-10 text-xs text-muted max-w-2xl">
        We do not promise a visa outcome. That would be dishonest. Every rule sourced from an official
        government portal, every fee verified, every deadline traceable.
      </div>
    </div>
  );
}


function ConciergeLeadForm() {
  const [open, setOpen] = useState(false);
  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [country, setCountry] = useState("");
  const [hostUniversity, setHostUniversity] = useState("");
  const [note, setNote] = useState("");
  const [sending, setSending] = useState(false);
  const [status, setStatus] = useState(null); // {ok:true} | {error:string}

  const submit = async (e) => {
    e.preventDefault();
    setStatus(null);
    if (!name.trim() || !email.trim()) {
      setStatus({ error: "Name and email are required." });
      return;
    }
    setSending(true);
    try {
      await api.post("/concierge/request", {
        name: name.trim(),
        email: email.trim(),
        country: country.trim() || undefined,
        host_university: hostUniversity.trim() || undefined,
        note: note.trim() || undefined,
      });
      setStatus({ ok: true });
      setName(""); setEmail(""); setCountry(""); setHostUniversity(""); setNote("");
    } catch (err) {
      setStatus({ error: err?.response?.data?.detail || "Couldn't send. Try again." });
    } finally {
      setSending(false);
    }
  };

  if (!open) {
    return (
      <button
        data-testid="concierge-lead-toggle"
        onClick={() => setOpen(true)}
        className="mt-3 text-[12px] font-semibold text-elec hover:underline text-left"
      >
        Prefer we reach out to you first? →
      </button>
    );
  }

  if (status?.ok) {
    return (
      <div data-testid="concierge-lead-success" className="mt-4 p-4 rounded-xl bg-white border border-ink/10">
        <div className="text-sm font-bold text-ink">Request received.</div>
        <p className="mt-1 text-[13px] text-muted leading-snug">
          Check your inbox. We've sent a confirmation. We'll follow up within one business day.
        </p>
        <button
          onClick={() => { setOpen(false); setStatus(null); }}
          className="mt-2 text-[12px] font-semibold text-elec hover:underline"
        >
          Close
        </button>
      </div>
    );
  }

  return (
    <form onSubmit={submit} data-testid="concierge-lead-form" className="mt-4 p-4 rounded-xl bg-white border border-ink/10 space-y-2">
      <div className="text-[10px] uppercase tracking-[0.2em] text-soft font-bold">Concierge. Request a callback</div>
      <div className="grid sm:grid-cols-2 gap-2">
        <input
          data-testid="concierge-lead-name"
          value={name} onChange={(e) => setName(e.target.value)}
          placeholder="Your name*" required maxLength={100}
          className="px-3 py-2 border border-ink/20 rounded-lg text-[13px] bg-white focus:outline-none focus:border-elec"
        />
        <input
          data-testid="concierge-lead-email"
          value={email} onChange={(e) => setEmail(e.target.value)}
          placeholder="Email*" type="email" required
          className="px-3 py-2 border border-ink/20 rounded-lg text-[13px] bg-white focus:outline-none focus:border-elec"
        />
        <input
          data-testid="concierge-lead-country"
          value={country} onChange={(e) => setCountry(e.target.value)}
          placeholder="Host country (e.g., Germany)" maxLength={60}
          className="px-3 py-2 border border-ink/20 rounded-lg text-[13px] bg-white focus:outline-none focus:border-elec"
        />
        <input
          data-testid="concierge-lead-university"
          value={hostUniversity} onChange={(e) => setHostUniversity(e.target.value)}
          placeholder="Host university" maxLength={160}
          className="px-3 py-2 border border-ink/20 rounded-lg text-[13px] bg-white focus:outline-none focus:border-elec"
        />
      </div>
      <textarea
        data-testid="concierge-lead-note"
        value={note} onChange={(e) => setNote(e.target.value)}
        placeholder="Anything specific we should know? (optional)"
        rows={2} maxLength={1000}
        className="w-full px-3 py-2 border border-ink/20 rounded-lg text-[13px] bg-white focus:outline-none focus:border-elec"
      />
      <div className="flex items-center gap-2 flex-wrap">
        <button
          data-testid="concierge-lead-submit"
          type="submit" disabled={sending}
          className="moe-btn moe-btn-primary !px-4 text-sm disabled:opacity-50"
        >
          {sending ? "Sending…" : "Send request"}
        </button>
        <button
          type="button"
          onClick={() => { setOpen(false); setStatus(null); }}
          className="text-[12px] font-semibold text-muted hover:text-ink"
        >
          Cancel
        </button>
      </div>
      {status?.error && (
        <div data-testid="concierge-lead-error" className="text-[12px] text-red-600">{status.error}</div>
      )}
    </form>
  );
}
